import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

/**
 * LF360-2024 — what the signed-in portal user may do in the current project.
 *
 * Read from GET /v1/permissions/mine whenever the current project changes. The
 * portal hides what the user cannot do; the server checks every request
 * anyway, so this is a courtesy, never the protection.
 *
 * Organization and System admins may do everything and are answered from the
 * user record straight away, without waiting for the request.
 */

export const PERMISSIONS_IDLE = "idle";
export const PERMISSIONS_LOADING = "loading";
export const PERMISSIONS_READY = "ready";

const initialState = {
  status: PERMISSIONS_IDLE,
  projectId: null,
  permissions: [],
  role: null,
  siteScoping: null,
};

export const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    permissionsLoading: (state, action) => ({ ...initialState, status: PERMISSIONS_LOADING, projectId: action.payload }),
    permissionsLoaded: (state, action) => {
      // An answer for a project the user has since left is not this project's.
      if (action.payload.projectId !== state.projectId) return state;
      return {
        status: PERMISSIONS_READY,
        projectId: action.payload.projectId,
        permissions: action.payload.permissions || [],
        role: action.payload.role || null,
        siteScoping: action.payload.siteScoping || null,
      };
    },
    permissionsCleared: () => initialState,
  },
});

export const { permissionsLoading, permissionsLoaded, permissionsCleared } = permissionsSlice.actions;

export const loadPermissions = (projectId) => async (dispatch) => {
  if (!projectId) {
    dispatch(permissionsCleared());
    return;
  }
  dispatch(permissionsLoading(projectId));
  try {
    const { data } = await axios.get("api/v1/permissions/mine", {
      headers: { "X-Project-Id": projectId },
    });
    dispatch(permissionsLoaded({ ...data, projectId }));
  } catch (e) {
    // Nothing granted on a failed read: the screens fall back to showing less.
    dispatch(permissionsLoaded({ projectId, permissions: [] }));
  }
};

const isOrgAdmin = (state) => ["admin", "systemAdmin"].includes(state.user?.role);

export const selectPermissionsReady = (state) =>
  isOrgAdmin(state) || state.permissions?.status === PERMISSIONS_READY;

/** Whether the user holds `key` — any of them, when given several. */
export const selectCan = (state, ...keys) =>
  isOrgAdmin(state) || keys.some((key) => (state.permissions?.permissions || []).includes(key));

export default permissionsSlice.reducer;
